import type { SoftphoneCallQuality } from './softphoneClient.types';

/**
 * The slice of a Twilio `RTCSample` the summary needs. The SDK emits one per second on the
 * Call's `sample` event; it carries more (byte counts, totals) that the roll-up doesn't use.
 * `packetsLostFraction` is already a percentage (the SDK's thresholds are `max: 1`, `maxAverage: 3`).
 * The audio levels are the SDK's 0–32767 volume mock of the legacy getStats scale.
 */
export interface CallQualitySample {
  packetsLostFraction?: number | null;
  jitter?: number | null;
  rtt?: number | null;
  mos?: number | null;
  codecName?: string | null;
  audioInputLevel?: number | null;
  audioOutputLevel?: number | null;
}

interface Running {
  sum: number;
  count: number;
  max: number;
  min: number;
}

function running(): Running {
  return { sum: 0, count: 0, max: Number.NEGATIVE_INFINITY, min: Number.POSITIVE_INFINITY };
}

function push(r: Running, value: number | null | undefined): void {
  if (typeof value !== 'number' || Number.isNaN(value)) return;
  r.sum += value;
  r.count += 1;
  if (value > r.max) r.max = value;
  if (value < r.min) r.min = value;
}

function round(n: number): number {
  return Math.round(n * 100) / 100;
}

function avgMax(r: Running): { avg: number; max: number } | null {
  return r.count === 0 ? null : { avg: round(r.sum / r.count), max: round(r.max) };
}

function avgMin(r: Running): { avg: number; min: number } | null {
  return r.count === 0 ? null : { avg: round(r.sum / r.count), min: round(r.min) };
}

/**
 * The longest run of consecutive samples whose level did not change — the SDK's own definition
 * of a flat (dead) audio stream, kept as a length rather than a threshold. A run is one sample
 * long by itself; two identical samples in a row make a run of two.
 */
class FlatRun {
  private last: number | null = null;
  private current = 0;
  longest = 0;

  push(level: number | null | undefined): void {
    if (typeof level !== 'number' || Number.isNaN(level)) {
      this.reset();
      return;
    }
    this.current = this.last === level ? this.current + 1 : 1;
    this.last = level;
    if (this.current > this.longest) this.longest = this.current;
  }

  /** Break the run without counting the gap — used while the mic is muted. */
  reset(): void {
    this.last = null;
    this.current = 0;
  }
}

/**
 * Accumulates one call's quality signals as they arrive and rolls them up at the end.
 *
 * Pure and SDK-free so it can be unit-tested with synthetic samples and reused by any client
 * that exposes RTC stats. The Twilio client feeds it from the Call's `warning`,
 * `warning-cleared`, `reconnecting`, `reconnected` and `sample` events and the AudioHelper's
 * `deviceChange`; the roll-up rides on the call's termination diagnostic (see
 * `SoftphoneCallQuality`).
 */
export class CallQualityTracker {
  private readonly warnings: string[] = [];
  private readonly active = new Set<string>();
  private reconnects = 0;
  private reconnectingMs = 0;
  private reconnectingSince: number | null = null;
  /** Media and signaling reconnect independently; the outage is over when BOTH have recovered. */
  private openReconnects = 0;
  private samples = 0;
  private readonly loss = running();
  private readonly jitter = running();
  private readonly rtt = running();
  private readonly mos = running();
  private readonly flatInput = new FlatRun();
  private readonly flatOutput = new FlatRun();
  private codec: string | null = null;
  private inputDevice: string | null = null;
  private deviceChanges = 0;
  private activeDeviceLost = 0;
  private readonly now: () => number;

  constructor(now: () => number = () => Date.now()) {
    this.now = now;
  }

  onWarning(name: string): void {
    if (!this.warnings.includes(name)) this.warnings.push(name);
    this.active.add(name);
  }

  onWarningCleared(name: string): void {
    this.active.delete(name);
  }

  onReconnecting(): void {
    // The SDK reports media (ICE) and signaling drops separately, each with its own `reconnected`.
    // One outage spans them all: it opens on the first drop and closes when the last path is back.
    this.openReconnects += 1;
    if (this.reconnectingSince != null) return;
    this.reconnects += 1;
    this.reconnectingSince = this.now();
  }

  onReconnected(): void {
    if (this.openReconnects === 0) return;
    this.openReconnects -= 1;
    if (this.openReconnects === 0) this.closeReconnect();
  }

  /**
   * The browser's audio device list changed. `lostActiveDevices` names the devices the call was
   * using that disappeared — a headset dropping — as opposed to one merely being plugged in.
   */
  onDeviceChange(lostActiveDevices: string[]): void {
    this.deviceChanges += 1;
    if (lostActiveDevices.length > 0) this.activeDeviceLost += 1;
  }

  /** The mic the call is using, read from its local track; the latest label wins. */
  setInputDevice(label: string | null): void {
    if (label != null && label.length > 0) this.inputDevice = label;
  }

  /**
   * One per-second sample. `muted` breaks the mic's flat run without counting it, the same
   * exemption the SDK applies to its `constant-audio-input-level` warning: a muted mic is
   * supposed to be flat.
   */
  onSample(sample: CallQualitySample, options: { muted?: boolean } = {}): void {
    this.samples += 1;
    push(this.loss, sample.packetsLostFraction);
    push(this.jitter, sample.jitter);
    push(this.rtt, sample.rtt);
    push(this.mos, sample.mos);
    if (typeof sample.codecName === 'string' && sample.codecName.length > 0) this.codec = sample.codecName;
    if (options.muted === true) {
      this.flatInput.reset();
    } else {
      this.flatInput.push(sample.audioInputLevel);
    }
    this.flatOutput.push(sample.audioOutputLevel);
  }

  /** Roll everything up. A reconnect still open at the end counts up to now. */
  summary(edge: string | null): SoftphoneCallQuality {
    this.closeReconnect();
    return {
      degraded: this.warnings.length > 0 || this.reconnects > 0 || this.activeDeviceLost > 0,
      warnings: [...this.warnings],
      warningsActiveAtEnd: [...this.active],
      reconnects: this.reconnects,
      reconnectingSec: Math.round(this.reconnectingMs / 1000),
      samples: this.samples,
      packetsLostPct: avgMax(this.loss),
      jitterMs: avgMax(this.jitter),
      rttMs: avgMax(this.rtt),
      mos: avgMin(this.mos),
      codec: this.codec,
      edge,
      inputDevice: this.inputDevice,
      deviceChanges: this.deviceChanges,
      activeDeviceLost: this.activeDeviceLost,
      longestFlatInputSec: this.flatInput.longest,
      longestFlatOutputSec: this.flatOutput.longest,
    };
  }

  private closeReconnect(): void {
    if (this.reconnectingSince == null) return;
    this.reconnectingMs += Math.max(0, this.now() - this.reconnectingSince);
    this.reconnectingSince = null;
    this.openReconnects = 0;
  }
}
